import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, Calendar, Sun, Moon, Check, Settings } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OPTIONS = [
  { value: 'saturday', label: 'السبت' },
  { value: 'sunday', label: 'الأحد' },
  { value: 'monday', label: 'الإثنين' },
  { value: 'tuesday', label: 'الثلاثاء' },
  { value: 'wednesday', label: 'الأربعاء' },
  { value: 'thursday', label: 'الخميس' },
  { value: 'friday', label: 'الجمعة' },
];

export default function WorkScheduleSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-schedule/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSchedule(response.data);
    } catch (err) {
      console.error('Error fetching schedule:', err);
    }
    setLoading(false);
  };

  const activateSchedule = async (scheduleType) => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/work-schedule/activate/${scheduleType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(`تم تفعيل جدول الدوام ${scheduleType === 'ramadan' ? 'الرمضاني' : 'العادي'} بنجاح`);
      fetchSchedule();
    } catch (err) {
      setMessage('حدث خطأ أثناء تغيير جدول الدوام');
    }
    setSaving(false);
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time;
  };

  const getDayLabel = (dayValue) => {
    const day = DAYS_OPTIONS.find(d => d.value === dayValue);
    return day ? day.label : dayValue;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-body">هذه الصفحة متاحة للمدير فقط</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-gold text-white rounded-md font-body"
          >
            العودة للوحة التحكم
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">إعدادات الدوام</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* رسالة النظام */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('نجاح') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin w-8 h-8 border-4 border-gold border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 font-body">جاري التحميل...</p>
          </div>
        ) : (
          <>
            {/* جدول الدوام الحالي */}
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-gold" />
                <h2 className="text-xl font-heading font-bold text-primary">جدول الدوام الحالي</h2>
                <span className={`px-3 py-1 rounded-full text-sm font-body ${
                  currentSchedule?.schedule_type === 'ramadan' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                }`}>
                  {currentSchedule?.schedule_type === 'ramadan' ? 'دوام رمضان' : 'دوام عادي'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* أيام العمل */}
                <div>
                  <p className="text-sm text-gray-500 font-body mb-2">أيام العمل:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentSchedule?.work_days?.map(day => (
                      <span key={day} className="px-3 py-1 bg-sand rounded-full text-sm font-body">
                        {getDayLabel(day)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* أوقات الدوام */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Sun className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-sm text-gray-500 font-body">الفترة الصباحية:</p>
                      <p className="font-body font-semibold">
                        {formatTime(currentSchedule?.morning_start)} - {formatTime(currentSchedule?.morning_end)}
                      </p>
                    </div>
                  </div>
                  
                  {currentSchedule?.evening_start && (
                    <div className="flex items-center gap-4">
                      <Moon className="w-5 h-5 text-indigo-500" />
                      <div>
                        <p className="text-sm text-gray-500 font-body">الفترة المسائية:</p>
                        <p className="font-body font-semibold">
                          {formatTime(currentSchedule?.evening_start)} - {formatTime(currentSchedule?.evening_end)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* خيارات الدوام */}
            <h3 className="text-lg font-heading font-bold text-primary mb-4">تغيير نوع الدوام</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* الدوام العادي */}
              <div 
                className={`bg-white rounded-xl p-6 shadow-lg border-2 cursor-pointer transition-all hover:shadow-xl ${
                  currentSchedule?.schedule_type === 'normal' ? 'border-green-500' : 'border-transparent'
                }`}
                onClick={() => activateSchedule('normal')}
                data-testid="normal-schedule-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Sun className="w-8 h-8 text-yellow-500" />
                    <h3 className="text-lg font-heading font-bold text-primary">الدوام العادي</h3>
                  </div>
                  {currentSchedule?.schedule_type === 'normal' && (
                    <Check className="w-6 h-6 text-green-500" />
                  )}
                </div>
                <div className="space-y-2 text-sm font-body text-gray-600">
                  <p><strong>الأيام:</strong> السبت - الخميس</p>
                  <p><strong>الوقت:</strong> 8:00 صباحاً - 5:00 مساءً</p>
                </div>
                <button
                  disabled={saving || currentSchedule?.schedule_type === 'normal'}
                  className={`mt-4 w-full py-2 rounded-lg font-body transition-all ${
                    currentSchedule?.schedule_type === 'normal' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gold text-white hover:bg-gold-dark'
                  }`}
                >
                  {currentSchedule?.schedule_type === 'normal' ? 'مُفعّل' : 'تفعيل'}
                </button>
              </div>

              {/* دوام رمضان */}
              <div 
                className={`bg-white rounded-xl p-6 shadow-lg border-2 cursor-pointer transition-all hover:shadow-xl ${
                  currentSchedule?.schedule_type === 'ramadan' ? 'border-purple-500' : 'border-transparent'
                }`}
                onClick={() => activateSchedule('ramadan')}
                data-testid="ramadan-schedule-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Moon className="w-8 h-8 text-indigo-500" />
                    <h3 className="text-lg font-heading font-bold text-primary">دوام رمضان</h3>
                  </div>
                  {currentSchedule?.schedule_type === 'ramadan' && (
                    <Check className="w-6 h-6 text-purple-500" />
                  )}
                </div>
                <div className="space-y-2 text-sm font-body text-gray-600">
                  <p><strong>الأيام:</strong> السبت - الخميس</p>
                  <p><strong>الفترة الصباحية:</strong> 10:00 صباحاً - 4:00 مساءً</p>
                  <p><strong>الفترة المسائية:</strong> 10:00 مساءً - 2:00 صباحاً</p>
                </div>
                <button
                  disabled={saving || currentSchedule?.schedule_type === 'ramadan'}
                  className={`mt-4 w-full py-2 rounded-lg font-body transition-all ${
                    currentSchedule?.schedule_type === 'ramadan' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-gold text-white hover:bg-gold-dark'
                  }`}
                >
                  {currentSchedule?.schedule_type === 'ramadan' ? 'مُفعّل' : 'تفعيل'}
                </button>
              </div>
            </div>

            {/* ملاحظة */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-body">
                <strong>ملاحظة:</strong> تغيير جدول الدوام سيؤثر على حساب ساعات العمل للموظفين وتحديد حالة التأخير والغياب.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
